import { useEffect, useRef } from 'react'

import { showVersionToast } from '../staleChunkReload'

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000 // 5 minutes

interface VersionCheckOptions {
  /** Override the current build version (for testing). */
  buildVersion?: string
  /** Override the polling interval in ms (for testing). */
  interval?: number
  /** Override the enabled check (for testing). */
  enabled?: boolean
}

/**
 * Polls /version.json to detect when a new version has been deployed.
 * Shows a non-intrusive toast prompting the user to refresh.
 *
 * - Pauses polling when the tab is hidden (Visibility API).
 * - Does an immediate check when the tab becomes visible.
 * - Stops polling after detecting a version mismatch.
 * - Silently ignores fetch failures.
 * - Disabled in development mode.
 */
export function useVersionCheck(options?: VersionCheckOptions): void {
  const buildVersion = options?.buildVersion ?? import.meta.env.VITE_VERSION
  const interval = options?.interval ?? VERSION_CHECK_INTERVAL
  const enabled = options?.enabled ?? (!import.meta.env.DEV && !!buildVersion)

  const mismatchDetected = useRef(false)
  const intervalRef = useRef<ReturnType<typeof setInterval>>()

  useEffect(() => {
    if (!enabled) return

    async function checkVersion(): Promise<void> {
      if (mismatchDetected.current) return

      try {
        const res = await fetch('/version.json', { cache: 'no-cache' })
        if (!res.ok) return

        const data = await res.json()
        if (data.version && data.version !== buildVersion) {
          mismatchDetected.current = true
          showVersionToast()
          if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = undefined
          }
        }
      } catch {
        // Silently ignore fetch failures
      }
    }

    function startPolling(): void {
      if (intervalRef.current) return
      intervalRef.current = setInterval(checkVersion, interval)
    }

    function stopPolling(): void {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = undefined
      }
    }

    function handleVisibilityChange(): void {
      if (mismatchDetected.current) return

      if (document.hidden) {
        stopPolling()
      } else {
        checkVersion()
        startPolling()
      }
    }

    // Immediate check on mount, then poll
    checkVersion()
    startPolling()
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      stopPolling()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [enabled, buildVersion, interval])
}
